import React, { useEffect, useRef } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
// Bump this when the avatar PNGs on the backend change. Appended as `?v=...`
// to the asset URLs so browsers fetch the new bytes immediately and don't
// serve the stale year-cached portrait. Keep it short (date or short hash).
const AVATAR_V = "2026-06-29a";
// Same-origin asset URLs (proxied through our backend) so the canvas is not
// CORS-tainted and we can readback pixels for lip-sync.
const SASHA_URL = `${BACKEND}/api/assets/sasha-avatar?v=${AVATAR_V}`;
const CORINA_URL = `${BACKEND}/api/assets/corina-avatar?v=${AVATAR_V}`;

// Mouth-shape sequence cycled through while `speaking` is true. Each entry is
// an `mh` (mouth height in fraction of mouth-bbox-height) — small jaw at 0.18
// → wide vowel at 0.55. The pattern hits closed mouth twice per cycle so the
// motion reads as "talking" rather than just opening/closing.
const TALK_PATTERN = [0.18, 0.30, 0.50, 0.32, 0.42, 0.20, 0.55, 0.34, 0.22, 0.40];

/**
 * Animated portrait — stationary face with subtle CSS breathing/blinking,
 * plus a canvas-overlaid mouth that opens/closes ONLY while `speaking` is
 * true (driven by the chat's TTS lifecycle).
 *
 * Why this approach (not a looping video):
 *   • A looping head-shot reads as "fidgety" / AI-uncanny when nothing is
 *     being said. The user explicitly asked for stationary + authentic.
 *   • Canvas lip-sync gives us perfect alignment with the TTS playback —
 *     mouth moves the moment Sasha starts speaking, stops the moment she
 *     finishes. No client-side video has to be in sync with audio.
 *   • The static portrait loads instantly (~400 KB) vs a 1.6 MB WebM.
 *
 * Defaults to Sasha; pass `persona="corina"` on the Hosts concierge page.
 */
export default function SashaAvatar({
  active = false,
  speaking = false,
  listening = false,
  size = "md",
  showName = true,
  persona = "sasha",
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const imgRef = useRef(null);

  const portraitUrl = persona === "corina" ? CORINA_URL : SASHA_URL;
  const displayName = persona === "corina" ? "Corina" : "Sasha";
  const subline = persona === "corina" ? "Your hosts concierge" : "Your concierge";

  // Load the portrait once and draw it onto the canvas.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = portraitUrl;
    imgRef.current = img;

    const draw = () => {
      // Resize backing buffer to match CSS size × DPR for crisp rendering.
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const w = Math.max(1, Math.floor(rect.width * dpr));
      const h = Math.max(1, Math.floor(rect.height * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      // object-cover behaviour — preserve aspect ratio, crop to fill.
      const ir = img.naturalWidth / img.naturalHeight;
      const cr = canvas.width / canvas.height;
      let dw, dh, dx, dy;
      if (ir > cr) {
        dh = canvas.height;
        dw = ir * dh;
        dx = (canvas.width - dw) / 2;
        dy = 0;
      } else {
        dw = canvas.width;
        dh = dw / ir;
        dx = 0;
        dy = (canvas.height - dh) / 2;
      }
      ctx.drawImage(img, dx, dy, dw, dh);
    };

    img.onload = () => draw();
    const onResize = () => imgRef.current && imgRef.current.complete && draw();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [portraitUrl]);

  // Lip-sync loop — only runs while `speaking` is true.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!speaking) {
      // Redraw the clean portrait so any leftover mouth overlay is wiped.
      if (imgRef.current && imgRef.current.complete) {
        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const ir = imgRef.current.naturalWidth / imgRef.current.naturalHeight;
        const cr = canvas.width / canvas.height;
        let dw, dh, dx, dy;
        if (ir > cr) {
          dh = canvas.height;
          dw = ir * dh;
          dx = (canvas.width - dw) / 2;
          dy = 0;
        } else {
          dw = canvas.width;
          dh = dw / ir;
          dx = 0;
          dy = (canvas.height - dh) / 2;
        }
        ctx.drawImage(imgRef.current, dx, dy, dw, dh);
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    const ctx = canvas.getContext("2d");
    const start = performance.now();

    const tick = (t) => {
      if (!imgRef.current || !imgRef.current.complete) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      // 1) Repaint base portrait.
      const ir = imgRef.current.naturalWidth / imgRef.current.naturalHeight;
      const cr = canvas.width / canvas.height;
      let dw, dh, dx, dy;
      if (ir > cr) {
        dh = canvas.height;
        dw = ir * dh;
        dx = (canvas.width - dw) / 2;
        dy = 0;
      } else {
        dw = canvas.width;
        dh = dw / ir;
        dx = 0;
        dy = (canvas.height - dh) / 2;
      }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(imgRef.current, dx, dy, dw, dh);

      // 2) Compute mouth open amount from the talk pattern (~6 Hz cycle).
      const elapsed = (t - start) / 1000;
      const step = Math.floor(elapsed * 6) % TALK_PATTERN.length;
      const next = (step + 1) % TALK_PATTERN.length;
      const frac = (elapsed * 6) - Math.floor(elapsed * 6);
      const mh = TALK_PATTERN[step] * (1 - frac) + TALK_PATTERN[next] * frac;

      // 3) Mouth bounding box — empirically tuned for the Sasha portrait.
      //    Adjust persona-specific offsets if Corina's face sits elsewhere.
      const mouthCenterX = dx + dw * (persona === "corina" ? 0.50 : 0.495);
      const mouthCenterY = dy + dh * (persona === "corina" ? 0.685 : 0.705);
      const mouthW = dw * 0.085;
      const mouthH = dh * 0.025 * (1 + mh * 4); // grows with mh

      // Soft shadow under lower lip when mouth is open
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.35)";
      ctx.shadowBlur = Math.max(2, mh * 14);
      ctx.fillStyle = `rgba(56,18,22,${0.78 + mh * 0.2})`; // dark interior
      ctx.beginPath();
      ctx.ellipse(mouthCenterX, mouthCenterY, mouthW, mouthH, 0, 0, Math.PI * 2);
      ctx.fill();
      // Lip highlight — thin top arc
      ctx.shadowBlur = 0;
      ctx.strokeStyle = `rgba(170,80,80,${0.5 + mh * 0.4})`;
      ctx.lineWidth = Math.max(1, dw * 0.004);
      ctx.beginPath();
      ctx.ellipse(mouthCenterX, mouthCenterY, mouthW * 0.95, mouthH * 0.95, 0, Math.PI * 1.05, Math.PI * 1.95);
      ctx.stroke();
      ctx.restore();

      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [speaking, persona]);

  const sizes = {
    xs: "max-w-[88px]",
    sm: "max-w-[180px]",
    md: "max-w-[280px]",
    lg: "max-w-sm",
    xl: "max-w-md",
  };
  return (
    <div className={`relative w-full ${sizes[size]} mx-auto`} data-testid={`avatar-${persona}`}>
      <div className="aspect-[3/4]">
        <div
          className={`avatar-arch sasha-breathing w-full h-full ${
            speaking ? "sasha-talking" : listening ? "sasha-listening" : ""
          }`}
          style={{ position: "relative" }}
        >
          <canvas
            ref={canvasRef}
            className="w-full h-full block"
            data-testid={`avatar-canvas-${persona}`}
          />
        </div>

        {active && !speaking && (
          <div className="absolute inset-0 rounded-t-full pulse-ring pointer-events-none" />
        )}
        {listening && (
          <div
            className="absolute -inset-2 rounded-t-full pointer-events-none"
            style={{
              border: "2px solid var(--burgundy)",
              animation: "pulseRing 1.4s ease-out infinite",
            }}
          />
        )}
      </div>

      {showName && (
        <div className="text-center mt-4" data-testid={`avatar-name-${persona}`}>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--gold)]">
            {subline}
          </div>
          <div className="font-display text-2xl text-[var(--emerald)] mt-1 leading-none">
            {displayName}
          </div>
        </div>
      )}
    </div>
  );
}

export { SASHA_URL, CORINA_URL };
